import { PubSubSharedActions } from "@selector/shared";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  getOutgoers,
  type Node,
  type NodeChange,
  reconnectEdge,
} from "@xyflow/react";
import pluralize from "pluralize";
import PubSub from "pubsub-js";
import { clone, equals, filter, find, findIndex, map, prop, propEq } from "ramda";
import { match } from "ts-pattern";
import { CalloutIntent } from "widgets";
import { intersect } from "widgets/utils";
import type { StateCreator } from "zustand";

import { WorkflowActivityStatusEnum } from "@/graphql.ts";
import { isWorkflowRunning } from "@/modules/workflow/hooks/workflow.ts";
import type {
  WorkflowEditorStore,
  WorkflowReactFlowSlice,
  WorkflowReactFlowSliceState,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import { isSampleTypeSelectionRequired } from "@/modules/workflow/utils/sample-type.ts";
import { type EdgeData, type EditorNodeData, type WorkflowParameter } from "@/modules/workflow/utils/types.ts";

export const initialReactFlowSliceState: WorkflowReactFlowSliceState = {
  nodes: [],
  edges: [],
  instance: null,
  showMiniMap: false,
  viewport: { x: 0, y: 0, zoom: 1 },
  currentConnection: null,
  selectedActivity: null,
  isDragging: false,
};

export const createReactFlowSlice: StateCreator<WorkflowEditorStore, [], [], WorkflowReactFlowSlice> = (set, get) => {
  PubSub.subscribe(PubSubSharedActions.LOGOUT, () => {
    set(initialReactFlowSliceState);
  });

  return {
    ...initialReactFlowSliceState,
    onInit(instance) {
      set({ instance });
    },
    onNodesDelete(_changes) {
      // findSubgraphTopNodes can not be used here as get().edges is already outdated.
      // we would need to have access to the state before deletion as this function is
      // called AFTER the node deletion
      if (_changes.length === get().nodes.length) {
        void get().wipeTemporaryData();
      } else {
        void get().wipeTemporaryData(_changes.map((c) => c.id));
      }
    },
    onNodesChange(_changes) {
      let changes = _changes;

      const isRunning = isWorkflowRunning(get().workflowEvent.extendedExecutionStatus);
      const sampleTypeSelectionRequired = isSampleTypeSelectionRequired(get().getParameters(), get().sampleTypeId);

      // We can't edit while running
      if (isRunning) {
        // We just keep the selection action as we need to be able to see logs and files output while running
        changes = filter(propEq("select", "type"), _changes);
      } else {
        if (changes.some((change) => ["add", "position"].includes(change.type))) {
          if (!get().isDragging) {
            get().pushTimelineData({
              nodes: applyNodeChanges(changes, get().nodes) as Node<EditorNodeData>[],
              edges: get().edges,
              parameters: [],
            });
          }
        }
        for (const change of changes) {
          match(change)
            .with({ type: "remove" }, (change) => {
              // Remove all errors
              void get().resetActivityNodeErrors(change.id);

              if (get().selectedActivity?.nodeId === change.id) {
                set({ selectedActivity: null });
              }

              set({
                sampleTypeMissing: sampleTypeSelectionRequired,
                // Delete parameters of the current activity
                parametersRecord: Object.fromEntries(
                  Object.entries(get().parametersRecord).filter(([key]) => key !== change.id),
                ),
              });
            })
            .with({ type: "select", selected: false }, (change) => {
              // Kind of Node blur()
              get().checkAllParameters(change.id);
              set({
                sampleTypeMissing: sampleTypeSelectionRequired,
              });
            })
            .with({ type: "position", dragging: true }, () => {
              if (!get().isDragging) {
                set({ isDragging: true });
              }
            })
            .with({ type: "position", dragging: false }, () => {
              set({ isDragging: false });
            });
        }
      }

      const { selectionChanges, otherChanges } = changes.reduce(
        (changeList, c) => {
          if (c.type === "select") {
            changeList.selectionChanges.push(c);
          } else {
            changeList.otherChanges.push(c);
          }
          return changeList;
        },
        {
          selectionChanges: [] as typeof changes,
          otherChanges: [] as typeof changes,
        },
      );

      // Apply selection change immediately
      set({ nodes: applyNodeChanges(selectionChanges, get().nodes) as Node<EditorNodeData>[] });

      // Apply other changes only when workflow is not running
      if (!isRunning) {
        // Apply nodes changes at the end because we need to clean errors first
        // FIXME: This triggers maximumUpdateDepth exceeded and rendering issues. However,
        //  it is required since we manage the nodes ourselves. If we don't update their position in real time
        //  they will not move on screen. We need to find out if we can improve the rendering of the nodes
        //  itself somehow. But this needs to be applied in the whole UI and requires deeper analysis.
        //  To fix this we need a follow-up after SEL11-944.
        set({ nodes: applyNodeChanges(otherChanges, get().nodes) as Node<EditorNodeData>[] });
        for (const change of otherChanges) {
          match(change).with({ type: "add" }, (change) => {
            // prefill all parameters with default values
            get().registerActivityParameters(change.item.id);
            get().setActivityComputing(change.item.id);
          });
        }
      }
    },
    onEdgesChange(changes) {
      // selection, deletion

      // We can't edit while running
      if (isWorkflowRunning(get().workflowEvent.extendedExecutionStatus)) return;

      set({ edges: applyEdgeChanges(changes, get().edges) as Edge<EdgeData>[] });
      get().checkConnections();
    },
    onDelete(changes) {
      /*
        High-Level callback for deletion of nodes or edges.
        It can be used for API calls or cleanups.

        Warning: This callback does not modify the state directly!
       */

      // Deletion of nodes and edges
      const { nodes, edges } = changes;
      const newNodes = get().nodes.filter((n) => !nodes.some((node) => node.id === n.id));
      const newEdges = get().edges.filter((e) => !edges.some((edge) => edge.id === e.id));

      const nodesIds = map(prop("id"), changes.nodes);
      const parameters = nodesIds.map((nodeId) => get().getParameters(nodeId)).flat();

      get().pushTimelineData({
        nodes: newNodes,
        edges: newEdges,
        parameters: parameters.length > 0 ? parameters : [],
      });
    },
    onReconnect(oldEdge, newConnection) {
      // new inputs or outputs of an existing connection

      // We can't edit while running
      if (isWorkflowRunning(get().workflowEvent.extendedExecutionStatus)) return;

      set({ edges: reconnectEdge(oldEdge, newConnection, get().edges) as Edge<EdgeData>[] });

      get().checkConnections();
    },
    onConnect(connection) {
      // New connection

      // We can't edit while running
      if (isWorkflowRunning(get().workflowEvent.extendedExecutionStatus)) return;

      const newEdges = addEdge({ ...connection, type: "activity" }, get().edges);
      get().pushTimelineData({
        nodes: get().nodes,
        edges: newEdges,
        parameters: [],
      });

      set({ edges: newEdges });

      get().checkConnections();
    },
    toggleMinimap() {
      set({ showMiniMap: !get().showMiniMap });
    },
    setViewport(x, y, zoom) {
      set({ viewport: { x, y, zoom } });
      get().instance?.setViewport({ x, y, zoom });
    },
    onSelectionChange({ nodes }) {
      const count = nodes.length;
      const currentActivity = get().selectedActivity;

      // Single selection
      if (count === 1) {
        const node: Node<EditorNodeData> = nodes[0] as Node<EditorNodeData>;
        set({
          selectedActivity: {
            nodeId: node.id,
            activityName: node.data.activityName,
            version: node.data.version,
          },
        });
        return;
      }

      // No selection - only clear if not an activity list selection
      if (count === 0 && currentActivity?.nodeId === undefined) {
        return; // Keep activity list selection
      }

      // Multi-selection or no selection
      // FIXME: This resets the selection on undo, but not on redo.
      //  I will not fix it in SEL11-1893 but it requires a bug ticket for analysis
      set({ selectedActivity: null });
    },
    setCurrentConnection(connection) {
      // Remove handle i/o description on pointer hover
      get().setStatusBarMessage(null);

      // We can't edit while running
      if (isWorkflowRunning(get().workflowEvent.extendedExecutionStatus)) return;

      if (connection?.nodeId) {
        const node = get().getNode(connection.nodeId);
        if (node) {
          const activity = get().getActivity(node.data.activityName, node.data.version);
          if (activity) {
            if (connection.handleType === "source" && activity.outputs) {
              for (const output of activity.outputs) {
                if (output.id === connection.handleId) {
                  set({ currentConnection: { ...connection, compatibleTypes: output.types } });
                  return;
                }
              }
            } else if (connection.handleType === "target" && activity.inputs) {
              for (const input of activity.inputs) {
                if (input.id === connection.handleId) {
                  set({ currentConnection: { ...connection, compatibleTypes: input.compatibleTypes } });
                  return;
                }
              }
              return;
            }
          }
        }
      }

      set({ currentConnection: null });
    },
    getNode: (nodeId) => {
      return find((node) => node.id === nodeId, get().nodes) ?? null;
    },
    getParameters(nodeId?: string): WorkflowParameter[] {
      if (!nodeId) return Object.values(get().parametersRecord).flat() ?? [];
      return get().parametersRecord?.[nodeId] ?? [];
    },
    updateNodesMetadata(changes) {
      let lastRunningActivityName: string | null = get().workflowEvent.lastRunningActivityName ?? null;
      const nodes = clone(get().nodes);

      for (const { nodeId, data } of changes) {
        const idx = findIndex((node) => node.id === nodeId, nodes);

        if (idx >= 0) {
          if (data.status === WorkflowActivityStatusEnum.Running) {
            lastRunningActivityName =
              get().getActivity(nodes[idx].data.activityName, nodes[idx].data.version)?.label ?? null;
          }

          nodes[idx].data = { ...nodes[idx].data, ...data };
        }
      }

      set({ nodes, workflowEvent: { ...get().workflowEvent, lastRunningActivityName } });
    },
    isValidConnection({ source, sourceHandle, target, targetHandle }, showErrorInStatusBar = true) {
      if (showErrorInStatusBar) get().setStatusBarMessage(null);

      const edges = get().edges;
      const nodes = get().nodes;

      // source and target are nodeIds
      if (equals(source, target)) {
        // Don't connect to itself
        return false;
      }

      if (source && target) {
        const sourceNode = get().getNode(source);
        const targetNode = get().getNode(target);

        if (sourceNode && targetNode) {
          const sourceActivity = get().getActivity(sourceNode.data.activityName, sourceNode.data.version);
          const targetActivity = get().getActivity(targetNode.data.activityName, targetNode.data.version);

          const sourceOutput = find((output) => output.id === sourceHandle, sourceActivity?.outputs ?? []);
          const targetInput = find((input) => input.id === targetHandle, targetActivity?.inputs ?? []);

          const hasCycle = (node: Node<EditorNodeData>, visited = new Set<string>()) => {
            if (visited.has(node.id)) return false;

            visited.add(node.id);

            for (const outgoer of getOutgoers(node, nodes, edges)) {
              if (outgoer.id === source) return true;
              if (hasCycle(outgoer, visited)) return true;
            }

            return false;
          };

          if (hasCycle(targetNode)) {
            if (showErrorInStatusBar)
              get().setStatusBarMessage("You are creating an infinite cycle", CalloutIntent.Danger);
            return false;
          }

          // Check compatibility
          if (
            targetInput != undefined &&
            sourceOutput != undefined &&
            !intersect(targetInput.compatibleTypes, sourceOutput.types)
          ) {
            if (showErrorInStatusBar)
              get().setStatusBarMessage("Input and output are not compatible", CalloutIntent.Warning);
            return false;
          }

          // Min, max connections
          if (targetInput) {
            // Can be the same connection again, and it should be valid of course
            const connections = filter(
              (edge) =>
                (edge.source !== source || (edge.source === source && edge.sourceHandle !== sourceHandle)) &&
                edge.target === target &&
                edge.targetHandle === targetHandle,
              edges,
            );

            // Only show if it's too many connections or already max in this validation case
            if (targetInput.maxConnections != undefined && connections.length >= targetInput.maxConnections) {
              if (showErrorInStatusBar) {
                get().setStatusBarMessage(
                  `Only ${targetInput.maxConnections} ${pluralize(
                    "connection",
                    targetInput.maxConnections,
                  )} allowed at a time`,
                  CalloutIntent.Warning,
                );
              }
              return false;
            }
          }
        }
      }

      return true;
    },
    deselectAll() {
      const nodes = get().nodes;
      const nodeChanges: NodeChange[] = map(
        (node) => ({
          id: node.id,
          type: "select",
          selected: false,
        }),
        nodes,
      );
      const edges = get().edges;
      const edgeChanges: EdgeChange[] = map(
        (edge) => ({
          id: edge.id,
          type: "select",
          selected: false,
        }),
        edges,
      );
      set({
        nodes: applyNodeChanges(nodeChanges, nodes) as Node<EditorNodeData>[],
        edges: applyEdgeChanges(edgeChanges, edges) as Edge<EdgeData>[],
        // selectedActivity: null,
        // selectedNodesRefCounter: 0,
      });
    },
    setSelectedActivity(selectedActivity) {
      set({ selectedActivity });
    },
  };
};
